import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: string | null;
}

/**
 * Global error boundary — évite l'écran blanc en cas de crash JS/render.
 * Affiche une page d'erreur claire avec un bouton "Réessayer" qui purge
 * le service worker et les caches puis recharge la page.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Log précis pour diagnostic (visible dans la console navigateur + Sentry-like)
    console.error("[ErrorBoundary] Crash React capturé:", error, info?.componentStack);
    this.setState({ info: info?.componentStack ?? null });
    try {
      const payload = {
        message: error?.message,
        stack: error?.stack,
        componentStack: info?.componentStack,
        url: typeof window !== "undefined" ? window.location.href : "",
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ts: new Date().toISOString(),
      };
      window.localStorage?.setItem("__cg_last_error", JSON.stringify(payload));
    } catch {
      /* noop */
    }
  }

  handleRetry = async () => {
    try {
      window.__forcePwaReset?.();
    } catch {
      /* noop */
    }
    // Fallback direct si le helper n'est pas dispo
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* noop */
    }
    const u = new URL(window.location.href);
    u.searchParams.set("_r", String(Date.now()));
    window.location.replace(u.toString());
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error?.message || "Erreur inconnue";

    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-xl text-card-foreground">
          <div className="text-4xl mb-3">😕</div>
          <h1 className="text-xl font-semibold mb-2">Une erreur est survenue</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Nous n'avons pas pu afficher cette page. Cela vient souvent d'une ancienne
            version en cache. Cliquez sur « Réessayer » pour recharger proprement.
          </p>
          <details className="mb-4 text-xs text-muted-foreground">
            <summary className="cursor-pointer select-none">Détails techniques</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-muted p-2 text-[11px] max-h-40 overflow-auto">
              {msg}
            </pre>
          </details>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleRetry}
              className="flex-1 rounded-lg bg-primary text-primary-foreground py-2.5 px-4 text-sm font-medium hover:opacity-90 transition"
            >
              Réessayer
            </button>
            <button
              onClick={this.handleHome}
              className="flex-1 rounded-lg border border-border py-2.5 px-4 text-sm font-medium hover:bg-muted transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
